import React, { useMemo } from 'react';
import { Button as AntButton, Popconfirm, Row, Tag } from 'antd';
import Button from 'components/Button';
import IActions from 'icons/IActions';
import { toCurrency } from 'utils/utils';
import AssetDetails from '../../Asset/AssetDetail';
import DeleteButton from 'components/DeleteButton';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import { AssetResponse } from 'services/assets';
import ICatalog from 'icons/ICatalog';
import SplitAssetButton from './components/SplitAssetButton';
import IrvScheduleModal from 'components/IrvScheduleModal';
import IrvScheduleButton from 'components/IrvScheduleButton';
import ModalControllerNV from 'components/ModalControllerNV';
import { isNil } from 'lodash';
import getEnvValue from 'utils/getEnvValue';
import TasksListPopover from 'pages/workflow/TasksListPopover';
import AssetMoveButton from 'components/AssetMoveButton';
import ButtonOperation from 'components/ButtonOperation';

export type AssetsTableProps = {
  currcode?: string;
  assets: any[];
  reftechProp?: () => void;
  onAddFromCatalog?: () => void;
  onAddFromCatalogPack?: () => void;
  onAddFromStock?: () => void;
  onAddInitAsset?: () => void;
  onCreateNewAsset?: () => void;
  onCreateNewMultiCompAsset?: () => void;
  onDelete: (asset: any) => void;
  onMoveToNewelement: (asset: any) => void;
  onSelect?: (asset: any) => void;
  ctfclevel?: string;
  phase: 'PROD' | 'PROPOSAL';
  flagreal: boolean;
  onSaveAsset?: (asset: any) => void;
  ctrid?: number;
  title?: string;
  fromproposal?: boolean;
  proposalData?: any;
  mfuse?: boolean;
  assetCatalog?: boolean;
  showActionButton?: boolean;
  hideSaveBtnAsset?: boolean;
  ctstatus?: string;
  nbreAsset?:any;
};

const AssetsTable = (props: AssetsTableProps) => {

  const {
    assets,
    onAddFromCatalog,
    onAddFromCatalogPack,
    onCreateNewAsset,
    onCreateNewMultiCompAsset,
    onAddFromStock,
    onAddInitAsset,
    onDelete,
    onMoveToNewelement,
    onSelect,
    ctfclevel = 'PROPOSAL',
    phase = 'PROPOSAL',
    flagreal,
    onSaveAsset,
    currcode,
    ctrid,
    title,
    reftechProp,
    fromproposal,
    proposalData,
    mfuse,
    assetCatalog,
    showActionButton,
    hideSaveBtnAsset,
    ctstatus,
    nbreAsset
    } = props;

  
  
  const compareNbreAsset = nbreAsset ? parseInt(nbreAsset.parvaluemin) <= assets?.length : false;

  const columns = useMemo<DataGridColDef<AssetResponse>[]>(
    () => [
      {
        comcode: 'Asset.assetreference',
        dataIndex: 'assetreference',
        render: (_, record) => (
          <AntButton
            type="link"
            onClick={() => {
              onSelect && onSelect(record);
            }}
          >
            Select
          </AntButton>
        ),

        fixed: fromproposal ? false : 'left',
        width: 140,
      },

      {
        comcode: 'Asset.assetdescription',
        dataIndex: 'assetlabel',
        width: 250,
        render: (assetlabel, record) => (
          <ModalControllerNV
            content={({ setVisible, visible }) => (
              <a onClick={() => setVisible(true)}>{assetlabel}</a>
            )}
            modal={({ setVisible, visible }) => (
              <AssetDetails
                assetid={record.assetid}
                asset={record}
                onCancel={() => setVisible(false)}
                showAsModal
                display
                isproposalcreate={isNil(ctrid)}
                visible={visible}
                onSave={(asset) => {
                  setVisible(false);
                  onSaveAsset && onSaveAsset(asset);
                }}
                hideSaveBtn={hideSaveBtnAsset}
                phaseprop={record.assetstatus === 'REMARK' ? 'PROD' : phase}
                flagreal={flagreal}
                fromproposal={fromproposal}
                proposalData={proposalData}
                ctstatus={ctstatus}
              />
            )}
          />
        ),
      },
      {
        comcode: 'Asset.assettypelabel',
        dataIndex: 'assettypelabel',
        width: 200,
      },
      {
        comcode: 'Asset.assetcategorylabel',
        dataIndex: 'assetcategorylabel',
        width: 200,
      },
      {
        comcode: 'Asset.suppliername',
        dataIndex: 'suppliername',
        width: 250,
      },

      {
        comcode: 'Asset.assetstatuslabel',
        dataIndex: 'statuslabel',
        width: 100,
      },
      {
        comcode: 'Asset.assetdeliverydate',
        dataIndex: 'assetdeliverydate',
        width: 100,
      },
      {
        align: 'right',
        comcode: 'Asset.assetunitnetamount',
        dataIndex: 'assetunitnetamount',
        render: (val) =>
          toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 150,
      },
      {
        align: 'right',
        comcode: 'Asset.assetqty',
        dataIndex: 'assetqty',
        fixed: fromproposal ? false : 'right',
        width: 100,
      },
      {
        align: 'right',
        comcode: 'Asset.assetnetamount',
        dataIndex: 'assetnetamount',
        fixed: fromproposal ? false : 'right',
        render: (val) =>
          toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 150,
      },
      {
        align: 'right',
        comcode: 'Asset.assetrentalpart',
        dataIndex: 'rentalpart',
        fixed: fromproposal ? false : 'right',
        render: (val) =>
          toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 150,
      },
      {
        align: 'right',
        comcode: 'Asset.assetrvamount',
        dataIndex: 'assetrvamount',
        fixed: fromproposal ? false : 'right',
        render: (val) =>
          toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 150,
      },

      {
        align: 'right',
        comcode: 'Asset.upfront',
        dataIndex: 'upfront',
        fixed: fromproposal ? false : 'right',
        render: (val) =>
          toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 120,
      },
      {
        align: 'right',
        comcode: 'Asset.duration',
        dataIndex: 'duration',
        fixed: fromproposal ? false : 'right',
        width: 120,
      },
      {
        align: 'right',
        comcode: 'Asset.rental',
        dataIndex: 'rental',
        fixed: fromproposal ? false : 'right',
        render: (val) =>
          toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 120,
      },
      {
        align: 'right',
        comcode: 'Asset.assetfinamount',
        dataIndex: 'assetfinamount',
        fixed: fromproposal ? false : 'right',
        render: (val) =>
          toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 150,
      },

      {
        align: 'right',
        comcode: 'Asset.assettaggrouping',
        dataIndex: 'assettaggrouping',
        fixed: fromproposal ? false : 'right',
        render: (val) => (
          <Tag color="blue" key={val}>
            {val}
          </Tag>
        ),
        width: 150,
      },
      {
        key: 'actions',
        comcode: 'actions',
        render: (_, record) => (
          <>
            {ctrid! > 0 &&
            record.assetqty > 1 &&
            !['ORDER', 'DELIVER'].includes(record.assetstatus) ? (
              <SplitAssetButton
                reftechProp={reftechProp}
                assetid={record.assetid}
                comcode="split"
                ctrid={ctrid!}
                qty={record.assetqty}
              />
            ) : null}
            {phase === 'PROPOSAL' && mfuse === false && (
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  onDelete(record);
                }}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            )}

            {record.irvSchedule?.length > 0 && (
              <IrvScheduleModal
                irvSchedule={record.irvSchedule}
                currcode={record.currcode}
                controller={
                  <IrvScheduleButton
                    comcode="irvScheduleButton"
                    twoToneColor="red"
                    style={{
                      height: 40,
                      marginLeft: 5,
                      marginRight: 5,
                      marginBottom: 5,
                    }}
                    onlyicon
                  />
                }
              />
            )}
            {phase === 'PROPOSAL' && assets?.length > 1 && mfuse === false && (
              <Popconfirm
                title="Sure to Move to a new element ?"
                onConfirm={() => {
                  onMoveToNewelement(record);
                }}
              >
                <AssetMoveButton
                  twoToneColor="red"
                  comcode="Move"
                  style={{ width: 40, height: 40 }}
                  onlyicon
                />
              </Popconfirm>
            )}
            {ctrid! > 0 && showActionButton && (
              <TasksListPopover
                status={record?.assetstatus ?? ''}
                statusdate={record?.assetstatusdate ?? ''}
                entityId={record.assetid}
                entityReference={record?.assetreference}
                entityName="ASSET"
                title={record?.assetreference + ' - ' + record?.assetlabel}
              >
                <Button
                  comcode="actions"
                  style={{
                    height: 40,
                    marginLeft: 5,
                    marginRight: 5,
                    marginBottom: 5,
                  }}
                  icon={<IActions />}
                />
              </TasksListPopover>
            )}
          </>
        ),
        fixed: 'right',
        width: ctrid ? 220 : 80,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onDelete, onSelect, onSaveAsset, assets, phase, assetCatalog, currcode]
  );

  return (
    <>
      <Row justify="end" gutter={30}>
        {phase === 'PROPOSAL' && mfuse === false && onAddFromStock && (
          <ButtonOperation
            comcode="AddFromStock"
            icon={<ICatalog />}
            type="default"
            onClick={onAddFromStock}
            custom={{ height: 45, marginRight: 10 }}
            disabled={compareNbreAsset}
          />
        )}
        {phase === 'PROPOSAL' && mfuse === false && onAddInitAsset && (
          <ButtonOperation
            comcode="AddInitAsset"
            icon={<ICatalog />}
            type="default"
            onClick={onAddInitAsset}
            custom={{ height: 45, marginRight: 10 }}
            disabled={compareNbreAsset}
          />
        )}

        {phase === 'PROPOSAL' && mfuse === false && onAddFromCatalog && (
          <ButtonOperation
            comcode="addFromCatalog"
            icon={<ICatalog />}
            type="default"
            onClick={onAddFromCatalog}
            custom={{ height: 45, marginRight: 10 }}
            disabled={compareNbreAsset}
          />
        )}

        {phase === 'PROPOSAL' && mfuse === false && onAddFromCatalogPack && (
          <ButtonOperation
            comcode="addAssetPack"
            icon={<ICatalog />}
            type="default"
            onClick={onAddFromCatalogPack}
            custom={{
              height: 45,
              backgroundColor: getEnvValue('secondarycolor'),
              color: 'white',
              marginRight: 10,
            }}
            disabled={compareNbreAsset}
          />
        )}
        {phase === 'PROPOSAL' && mfuse === false && onCreateNewAsset && (
          <ButtonOperation
            comcode="createNewAsset"
            type="primary"
            onClick={onCreateNewAsset}
            custom={{ height: 45, minWidth: 130 }}
            disabled={compareNbreAsset}
          />
        )}
      </Row>
      <DataGrid
        size="middle"
        columns={
          ctfclevel === 'ASSET'
            ? columns
            : columns.filter(
                (ea) =>
                  ea.dataIndex !== 'rental' &&
                  ea.dataIndex !== 'upfront' &&
                  ea.dataIndex !== 'duration' &&
                  ea.dataIndex !== 'assetreference'
              )
        }
        dataSource={assets}
        titleComcode={title ?? undefined}
        rowKey="assetid"
        pagination={false}
      />
    </>
  );
};

export default AssetsTable;
